import { useState, useEffect, useRef } from "react";
import { T } from "../../config/theme.js";
import { Input } from "../ui/index.jsx";
import { api } from "../../hooks/useApi.js";

export default function StockSearch({ onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 1) {
      setResults([]);
      setShow(false);
      return;
    }

    // 300ms 디바운스
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api(`/stocks/search?q=${encodeURIComponent(query)}`);
        if (Array.isArray(data) && data.length > 0) {
          setResults(data);
          setShow(true);
        } else {
          setResults([]);
          setShow(false);
        }
      } catch (err) {
        console.error("[StockSearch] 검색 실패:", err);
        // API 실패 시 fallback 로컬 검색
        const q = query.toLowerCase();
        const matched = FALLBACK_STOCKS.filter(
          (s) => s.name.toLowerCase().includes(q) || s.code.includes(q)
        ).slice(0, 8);
        setResults(matched);
        setShow(matched.length > 0);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          fontSize: 13,
          color: T.textSub || T.textDim,
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        종목 검색 (이름 또는 코드)
      </div>
      <Input
        placeholder="삼성전자 또는 005930"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setShow(true)}
        onBlur={() => setTimeout(() => setShow(false), 200)}
      />
      {show && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 50,
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            maxHeight: 240,
            overflowY: "auto",
            marginTop: 4,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {results.map((s) => (
            <div
              key={s.code}
              style={{
                padding: "10px 14px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottom: `1px solid ${T.border}`,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = T.cardHover)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
              onMouseDown={() => {
                onSelect(s.code, s.name);
                setQuery(s.name);
                setShow(false);
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{ fontSize: 13, fontWeight: 600, color: T.text }}
                >
                  {s.name}
                </span>
                {s.market && (
                  <span
                    style={{
                      fontSize: 10,
                      color: T.textMuted,
                      background: `${T.border}88`,
                      padding: "1px 6px",
                      borderRadius: 4,
                    }}
                  >
                    {s.market}
                  </span>
                )}
              </div>
              <span style={{ fontSize: 12, color: T.textMuted }}>
                {s.code}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// API 실패 시 fallback용 로컬 목록
const FALLBACK_STOCKS = [
  { code: "005930", name: "삼성전자" },
  { code: "000660", name: "SK하이닉스" },
  { code: "373220", name: "LG에너지솔루션" },
  { code: "207940", name: "삼성바이오로직스" },
  { code: "005380", name: "현대차" },
  { code: "000270", name: "기아" },
  { code: "006400", name: "삼성SDI" },
  { code: "051910", name: "LG화학" },
  { code: "035420", name: "NAVER" },
  { code: "035720", name: "카카오" },
  { code: "005490", name: "POSCO홀딩스" },
  { code: "055550", name: "신한지주" },
  { code: "105560", name: "KB금융" },
  { code: "096770", name: "SK이노베이션" },
  { code: "028260", name: "삼성물산" },
  { code: "003670", name: "포스코퓨처엠" },
  { code: "066570", name: "LG전자" },
  { code: "034730", name: "SK" },
  { code: "003550", name: "LG" },
  { code: "012330", name: "현대모비스" },
];
